import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Column } from '@/types/common';
import {
  PO_STATUSES,
  PurchaseOrder,
  PurchaseOrderReceiptStatus,
  PurchaseOrderStatus,
  RECEIPT_STATUSES,
  StatusCount,
  warehouseApi,
  warehouseExport,
} from '@/services/api/warehouse';
import {
  ExportCsvButton,
  fmtDateTime,
  fmtQty,
  HubCell,
  ListCard,
  ListTab,
  metaCls,
  nameCls,
  PageHead,
  PO_STATUS,
  PO_TYPE_LABEL,
  poSourceOf,
  RECEIPT_STATUS,
  StatusBadge,
  StatusMeta,
  useListState,
  useLoad,
  WAREHOUSE_PATHS,
} from './shared';
import WarehouseFilter from './WarehouseFilter';
import { INBOUND_FILTERS, PO_FILTERS } from './filterFields';

/**
 * One page for both views of the same rows: Purchase order (every PO, tabbed
 * by PO status) and Inbound request (confirmed POs, tabbed by receipt
 * progress) — mirroring the logistic web.
 */
export type PoListMode = 'purchase-order' | 'inbound';

type Tab = 'ALL' | PurchaseOrderStatus | PurchaseOrderReceiptStatus;

const ProgressCell: React.FC<{ received: number; expected: number }> = ({
  received,
  expected,
}) => {
  const pct = expected > 0 ? Math.min(100, (received / expected) * 100) : 0;
  return (
    <div className="w-24">
      <p className="text-xs tabular-nums text-text-sub-600">
        {fmtQty(received)} / {fmtQty(expected)}
      </p>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-bg-weak-50">
        <div
          className={`h-full rounded-full ${pct >= 100 ? 'bg-success-base' : 'bg-primary'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const baseColumns = (mode: PoListMode): Column<PurchaseOrder>[] => [
  {
    key: 'code',
    title: 'PO number',
    sticky: 'left',
    render: (_v, x) => (
      <div>
        <p className="font-mono text-xs font-semibold text-primary">{x.code}</p>
        <p className={metaCls}>{PO_TYPE_LABEL[x.type ?? 'REGULAR']}</p>
      </div>
    ),
  },
  {
    key: 'source',
    title: 'Sumber',
    render: (_v, x) => {
      const src = poSourceOf(x);
      return (
        <div className="max-w-[180px]">
          <p className={`${nameCls} truncate`} title={src.value}>
            {src.value || '—'}
          </p>
          <p className={metaCls}>{src.label}</p>
        </div>
      );
    },
  },
  {
    key: 'hub',
    title: 'Hub Name',
    render: (_v, x) => <HubCell hub={x.hub} hubID={x.hubID} />,
  },
  {
    key: 'eta',
    title: 'Est. arrival',
    render: (_v, x) => (
      <span className="text-xs text-text-sub-600">
        {fmtDateTime(x.estArrivalAt)}
      </span>
    ),
  },
  ...(mode === 'inbound'
    ? [
        {
          key: 'received',
          title: 'Received',
          render: (_v: unknown, x: PurchaseOrder) => (
            <ProgressCell received={x.totalReceived} expected={x.totalQty} />
          ),
        },
        {
          key: 'receiptStatus',
          title: 'Status',
          render: (_v: unknown, x: PurchaseOrder) => (
            <StatusBadge map={RECEIPT_STATUS} value={x.receiptStatus} />
          ),
        },
      ]
    : [
        {
          key: 'lines',
          title: 'Lines / qty',
          align: 'right' as const,
          render: (_v: unknown, x: PurchaseOrder) => (
            <div className="text-right">
              <p className="text-sm tabular-nums text-text-strong-950">
                {fmtQty(x.totalQty)}
              </p>
              <p className={metaCls}>{x.lineCount} SKU</p>
            </div>
          ),
        },
        {
          key: 'status',
          title: 'Status',
          render: (_v: unknown, x: PurchaseOrder) => (
            <StatusBadge map={PO_STATUS} value={x.status} />
          ),
        },
      ]),
  {
    key: 'createdAt',
    title: 'Dibuat',
    render: (_v, x) => (
      <span className="text-xs text-text-sub-600">
        {fmtDateTime(
          mode === 'inbound' ? (x.confirmedAt ?? x.createdAt) : x.createdAt
        )}
      </span>
    ),
  },
];

const COPY: Record<
  PoListMode,
  { title: string; description: string; empty: string; path: string }
> = {
  'purchase-order': {
    title: 'Purchase order',
    description:
      'Purchase order barang masuk ke gudang Dash untuk Anda, dari draft sampai confirmed.',
    empty: 'Belum ada purchase order',
    path: WAREHOUSE_PATHS.purchaseOrder,
  },
  inbound: {
    title: 'Inbound request',
    description:
      'Purchase order yang sudah confirmed dan sedang ditunggu kedatangannya di hub — lengkap dengan progres penerimaan.',
    empty: 'Belum ada inbound request',
    path: WAREHOUSE_PATHS.inbound,
  },
};

const PurchaseOrderListPage: React.FC<{ mode: PoListMode }> = ({ mode }) => {
  const navigate = useNavigate();
  const q = useListState<Tab>('ALL');
  const search = q.search || undefined;
  const inbound = mode === 'inbound';
  const copy = COPY[mode];

  const list = useLoad(() => {
    const base = { search, page: q.page, limit: q.limit };
    const tab = q.tab === 'ALL' ? undefined : q.tab;
    return inbound
      ? warehouseApi.listInboundRequests(
          {
            ...base,
            receiptStatus: tab as PurchaseOrderReceiptStatus | undefined,
          },
          q.filters
        )
      : warehouseApi.listPurchaseOrders(
          { ...base, status: tab as PurchaseOrderStatus | undefined },
          q.filters
        );
  }, [inbound, q.tab, search, q.filterKey, q.page, q.limit]);
  // Tab counts: the list's search + filters, minus the tab — never disagree
  // with it.
  const summary = useLoad(
    (): Promise<StatusCount<string>> =>
      inbound
        ? warehouseApi.getInboundRequestSummary({ search }, q.filters)
        : warehouseApi.getPurchaseOrderSummary({ search }, q.filters),
    [inbound, search, q.filterKey]
  );

  const statusMap: Record<string, StatusMeta> = inbound
    ? RECEIPT_STATUS
    : PO_STATUS;
  const keys: Tab[] = ['ALL', ...(inbound ? RECEIPT_STATUSES : PO_STATUSES)];
  const tabs: ListTab<Tab>[] = keys.map((k) => ({
    key: k,
    label: k === 'ALL' ? 'All' : statusMap[k].label,
    count: summary.data
      ? k === 'ALL'
        ? summary.data.total
        : (summary.data.byStatus.find((b) => b.status === k)?.count ?? 0)
      : null,
  }));

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <PageHead
        title={copy.title}
        description={copy.description}
        actions={
          <ExportCsvButton
            what={copy.title}
            onExport={() => {
              const tab = q.tab === 'ALL' ? undefined : q.tab;
              return inbound
                ? warehouseExport.inboundRequests(
                    {
                      search,
                      receiptStatus: tab as
                        PurchaseOrderReceiptStatus | undefined,
                    },
                    q.filters
                  )
                : warehouseExport.purchaseOrders(
                    { search, status: tab as PurchaseOrderStatus | undefined },
                    q.filters
                  );
            }}
          />
        }
      />
      <ListCard<PurchaseOrder, Tab>
        tabs={tabs}
        activeTab={q.tab}
        onTabChange={q.setTab}
        searchPlaceholder="Cari PO number, vendor…"
        onSearch={q.setSearch}
        filter={
          <WarehouseFilter
            title={copy.title}
            fields={inbound ? INBOUND_FILTERS : PO_FILTERS}
            value={q.filters}
            onApply={q.setFilters}
          />
        }
        columns={baseColumns(mode)}
        rows={list.data?.data ?? []}
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        emptyTitle={copy.empty}
        onRowClick={(x) => navigate(`${copy.path}/${x.id}`)}
        page={q.page}
        limit={q.limit}
        total={list.data?.pagination.total ?? 0}
        onPageChange={q.setPage}
        onLimitChange={q.setLimit}
      />
    </div>
  );
};

export default PurchaseOrderListPage;
